import { useMediaQuery } from "@mui/material";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

type NotificationData = {
  id: number;
  ticket: string;
  title: string;
  description: string;
  event_date: string;
  guests: string;
  start_time: string;
};

type NotificationItem = {
  id: number;
  notification_type: string;
  data: NotificationData;
  created: string;
  is_viewed: boolean;
};

interface NotificationBellProps {
  newNotification: NotificationItem | NotificationItem[] | null;
  unreadNotifications: NotificationItem[];
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  newNotification,
  unreadNotifications,
}) => {
  const [open, setOpen] = useState(false);
  const [storedUnread, setStoredUnread] = useState<NotificationItem[]>([]);
  const [latestNotifications, setLatestNotifications] = useState<NotificationItem[]>([]);
  const bellRef = useRef<HTMLDivElement>(null);
  const isSmallMobile = useMediaQuery("(max-width:575px)");

  const navigate = useNavigate();

  // Handle outside click to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bellRef.current && !bellRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Merge new unread notifications into storedUnread
  useEffect(() => {
    setStoredUnread((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const newOnes = unreadNotifications.filter((n) => !existingIds.has(n.id));
      return [...prev, ...newOnes];
    });
  }, [unreadNotifications]);

  // Merge new real-time notifications into latestNotifications
  useEffect(() => {
    if (newNotification) {
      const incoming = Array.isArray(newNotification) ? newNotification : [newNotification];
      setLatestNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const uniqueNew = incoming.filter((n) => !existingIds.has(n.id));
        return [...uniqueNew, ...prev];
      });
    }
  }, [newNotification]);

  const handleNotificationClick = (notification: NotificationItem) => {
    const { notification_type, data } = notification;

    if (notification_type === "Ticket Event Task Reminder" && data?.ticket) {
      navigate(`/ticket/information/${data.ticket}`);
    } else {
      console.warn("No matching route for this notification type.");
    }
  };

  const totalCount = latestNotifications.length + storedUnread.length;

  return (
    <div className="position-relative" ref={bellRef}>
      <button
        className="btn btn-icon btn-sm btn-active-light-primary position-relative"
        onClick={() => setOpen(!open)}
        title="View Notifications"
      >
        <i className="bi bi-bell fs-1"></i>
        {totalCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {totalCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="shadow rounded-4 animate__animated animate__fadeIn"
          style={{
            position: "absolute",
            top: "120%",
            right: -55,
            width: isSmallMobile ? "320px" : "420px",
            maxHeight: "520px",
            overflowY: "auto",
            background: "linear-gradient(to bottom, #fefefe, #fafafa)",
            border: "1px solid #e3e3e3",
            zIndex: 999,
          }}
        >
          <div className="px-4 py-3 border-bottom bg-light fw-semibold text-dark">
            Notifications Center
          </div>

          {latestNotifications.length > 0 && (
            <div>
              <div className="px-4 pt-3 pb-1 text-primary fw-bold small">
                🔔 New Notifications
              </div>
              {latestNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  item={notification}
                  onClick={() => handleNotificationClick(notification)}
                  isUnread={true}
                  highlightColor="#e3f2fd"
                />
              ))}
            </div>
          )}

          {storedUnread.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1 text-secondary fw-bold text-dark small">
                📬 Unread Notifications
              </div>
              {storedUnread.map((n) => (
                <NotificationCard
                  key={n.id}
                  item={n}
                  onClick={() => handleNotificationClick(n)}
                  isUnread={true}
                  highlightColor="#f8f9fa"
                />
              ))}
            </>
          )}

          {latestNotifications.length === 0 && storedUnread.length === 0 && (
            <div className="text-center text-muted py-5">
              <i className="bi bi-inbox-fill fs-2 text-secondary"></i>
              <div className="mt-2" style={{ fontSize: "14px" }}>
                You're all caught up!
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NotificationCard: React.FC<{
  item: NotificationItem;
  onClick: () => void;
  isUnread: boolean;
  highlightColor: string;
}> = ({ item, onClick, isUnread, highlightColor }) => {
  return (
    <div
      onClick={onClick}
      className="d-flex align-items-start gap-3 px-4 py-3 border-bottom"
      style={{
        backgroundColor: isUnread ? highlightColor : "#fff",
        cursor: "pointer",
        transition: "background 0.2s",
        borderRadius: "10px",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = "#e0f7fa";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.backgroundColor = isUnread
          ? highlightColor
          : "#fff";
      }}
    >
      <div className="mt-1 text-info fs-5">
        <i className="bi bi-lightning-charge-fill"></i>
      </div>
      <div className="flex-grow-1">
        <div
          className="fw-semibold text-dark"
          style={{ fontWeight: isUnread ? 600 : 400 }}
        >
          {item.data.title}
        </div>
        <div className="text-muted small">
          Ticket: <strong>{item.data.ticket || "N/A"}</strong>
        </div>
        <div className="text-muted small">
          {new Date(item.created).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default NotificationBell;
